import type { GrupoDeNavegacao } from "@plantus/ui";

/**
 * O mapa do sistema — os mesmos recortes do backend.
 *
 * <p>Setor da indústria, não camada técnica ([ADR-0002]). É a mesma divisão de
 * `br.com.plantus.*` e de `src/setores/*`: quem entende um lado navega o outro
 * sem mapa.
 *
 * <h2>⚠️ Módulo de backend não é item de menu</h2>
 *
 * A primeira versão desta lista tinha os sete módulos de `regulatorio`, um a
 * um. Parecia arrumado e estava errado: <b>os dois recortes são por eixos
 * diferentes.</b>
 *
 * <p>O backend recorta por <b>responsabilidade de domínio</b> — que código é
 * dono de que regra. A navegação recorta por <b>o que a pessoa procura</b>.
 *
 * <p>Ficha de segurança, regularização e rotulagem têm vínculo obrigatório com
 * um produto: nenhuma existe solta. Como itens de menu, elas sugeriam registros
 * independentes — e, no caso da ficha, contradiziam a invariante que existe
 * para "impedir a volta do arquivo espalhado".
 *
 * <p>Sobram três destinos: <b>produtos</b> (a raiz do catálogo), <b>petições</b>
 * (ciclo próprio com a ANVISA) e <b>habilitações</b> (da empresa, não do
 * produto).
 *
 * <h2>Três níveis: setor → módulo → submódulo</h2>
 *
 * O mesmo eixo, um recuo mais fundo. Um item ganha filhos quando o que está
 * embaixo dele é <b>operação sobre ele</b>, e não um cadastro vizinho:
 * "Estoque e movimentação" e "Dispensação e pesagem" são coisas que se fazem
 * COM matéria-prima. Como irmãos de "Matérias-primas" eles pareciam três
 * registros independentes; dentro dela, dizem a verdade do modelo.
 *
 * <p>O terceiro nível é o último — o tipo `SubmoduloDeNavegacao` não tem
 * `itens`. Um quarto recuo não cabe numa gaveta de 256px nem se lê de relance.
 *
 * <h2>⚠️ Esta lista é temporária, e precisa sumir</h2>
 *
 * Quando o RBAC existir, a navegação passa a ser <b>construída a partir das
 * permissões que a API devolve</b> ([ADR-0004] §6) — e este arquivo não deve
 * conviver com aquilo. Um mapa fixo ao lado de um mapa por permissão é a
 * receita para os dois divergirem, com o fixo mostrando o que o outro esconde.
 *
 * <p>Enquanto isso, `rota: null` significa <b>tela ainda não construída</b>, e
 * nunca "você não tem acesso". A distinção importa: no dia do RBAC, o que a
 * pessoa não pode ver <b>não aparece</b>; o que não existe continua aparecendo.
 */
export const SETORES: readonly GrupoDeNavegacao[] = [
  {
    id: "regulatorio",
    rotulo: "Assuntos Regulatórios",
    natureza: "setor",
    itens: [
      // O `icone` é o que sobra do item quando a lateral é recolhida à faixa —
      // e por isso ele é obrigatório para quem tem rota, com teste guardando.
      // Um destino sem desenho sumiria do menu recolhido sem nada dizer que
      // existe, que é o mesmo defeito de guardar "os abertos" em vez dos
      // recolhidos, um andar acima.
      { id: "painel", rotulo: "Painel de risco", rota: "/", icone: "painel" },

      // Produto é a RAIZ. Ficha de segurança, regularização e rotulagem têm
      // vínculo OBRIGATÓRIO com um produto — nenhuma existe solta — e por isso
      // vivem dentro dele, não ao lado.
      //
      // A ficha é o caso que deixa isso mais claro: a invariante 1 do módulo
      // `sds` existe justamente para "impedir a volta do arquivo espalhado".
      // Um item de menu chamado "Fichas de segurança" re-sugere aquilo — a
      // interface argumentando contra o modelo.
      { id: "produtos", rotulo: "Produtos", rota: null },

      // Petição tem ciclo próprio com a ANVISA: número de protocolo, situação,
      // e exigências com prazo correndo. É substantivo, não faceta — dá para
      // trabalhar uma petição sem estar olhando um produto.
      { id: "peticoes", rotulo: "Petições", rota: null },

      // Habilitação é da EMPRESA, não do produto. Não há por onde alcançá-la
      // pelo catálogo.
      { id: "habilitacoes", rotulo: "Habilitações", rota: null },

      // Fornecedor é dado mestre de quatro donos — AR qualifica, Compras pede,
      // Produção recebe, Qualidade avalia desvio — e por isso o módulo é
      // TRANSVERSAL no backend. Aparece aqui porque quem QUALIFICA é Assuntos
      // Regulatórios: o item de menu segue quem faz o trabalho, não quem é
      // dono do dado.
      // Caminhão, e não prédio: fornecedor é quem ENTREGA o material. O prédio
      // fica reservado para "Empresas" — dois desenhos parecidos no mesmo menu
      // recolhido seriam a adivinhação que o ícone existe para evitar.
      {
        id: "fornecedores",
        rotulo: "Fornecedores",
        rota: "/regulatorio/fornecedores",
        icone: "fornecedores",
      },
    ],
  },
  {
    id: "producao",
    rotulo: "Produção",
    natureza: "setor",
    itens: [
      // O almoxarifado é operado pela Produção — o grupo não tem um setor de
      // Suprimentos (ADR-0010). A DECISÃO sobre o material continua sendo de
      // Qualidade: quem opera não libera.
      {
        id: "materias-primas",
        rotulo: "Matérias-primas",
        rota: "/producao/materias-primas",
        // Tambor: é o objeto físico em que a etiqueta deste sistema vai colada.
        icone: "materias-primas",

        // Estoque e dispensação eram IRMÃOS de "Matérias-primas", e estava
        // errado: são operações SOBRE a matéria-prima, não vizinhas dela. Lado
        // a lado, os três se liam como três cadastros independentes — e quem
        // procura o estoque de matéria-prima procura DENTRO de matérias-primas,
        // que é o eixo desta lista ("o que a pessoa procura").
        itens: [
          // ⚠️ RECIPIENTES é FILHO, e não irmão, por duas razões — e a
          // segunda é a que decide.
          //
          // A primeira é de eixo: a bombona só existe, hoje, para guardar
          // matéria-prima, e quem a procura procura DENTRO de
          // matérias-primas.
          //
          // A segunda é de DESENHO. Um item de segundo nível com rota
          // exige ícone — é o que sobra dele na faixa recolhida —, e o
          // ícone de um recipiente seria um tambor: exatamente a silhueta
          // que "Matérias-primas" já usa. Dois desenhos iguais no menu
          // recolhido são a adivinhação que o ícone existe para evitar
          // (ver `Icone.tsx`).
          { id: "recipientes", rotulo: "Recipientes", rota: "/producao/recipientes" },
          { id: "estoque", rotulo: "Estoque e movimentação", rota: null },

          // ⚠️ ESTA PODE MUDAR DE LUGAR, e isso está previsto.
          //
          // Hoje dispensar e pesar é operação de ALMOXARIFADO: separa-se o
          // material do estoque, pesa-se e entrega-se. Quando existir módulo de
          // ordem de produção, a dispensação passa a ser uma ETAPA da ordem —
          // ela deixa de responder "o que sai do estoque" e passa a responder
          // "o que esta ordem consumiu", que é outra pergunta e outro dono.
          //
          // Deixar escrito aqui é o que permite mover sem arqueologia: quem
          // fizer a ordem de produção acha a decisão, não a deduz.
          { id: "dispensacao", rotulo: "Dispensação e pesagem", rota: null },
        ],
      },
    ],
  },
  {
    id: "qualidade",
    rotulo: "Qualidade",
    natureza: "setor",
    observacao: "Análise e liberação de lote — depende da assinatura eletrônica.",
    itens: [],
  },
  {
    id: "empresas",
    rotulo: "Empresas",
    natureza: "transversal",
    itens: [{ id: "empresas-lista", rotulo: "As três empresas", rota: null }],
  },
  {
    id: "identity",
    rotulo: "Acessos",
    natureza: "transversal",
    itens: [
      { id: "usuarios", rotulo: "Usuários e papéis", rota: null },
      { id: "minha-conta", rotulo: "Minha conta", rota: null },
    ],
  },
];

/**
 * Toda rota que a navegação oferece — nos TRÊS níveis. Usada pelo teste que
 * impede link morto.
 *
 * <p>Descer até o submódulo não é detalhe: um destino de terceiro nível
 * apontando para rota inexistente falha do mesmo jeito silencioso — e ainda
 * está a um clique a mais de distância de alguém tropeçar nele.
 */
export const ROTAS_OFERECIDAS: readonly string[] = SETORES.flatMap((grupo) =>
  grupo.itens
    .flatMap((item) => [item.rota, ...(item.itens ?? []).map((filho) => filho.rota)])
    .filter((rota): rota is string => rota !== null),
);
